import * as React from 'react';

import { JsonDocument } from '../models/intentGraph.models';
import { deserializeToImmutableMap } from '../serialize/serialize';
import { createStructuredSelector, mconnect } from '../state/redux';
import { IStyledComponent, styledComponent } from '../theme';
import { IComponentProps, IComponentStateProps, IRootContainerStyles } from '../types';
import { validateIntentSchema, validationSchema } from '../utils/schemaValidation';

import { Header } from './components/header';
import { NodesList } from './components/nodeList';
import { getMainAppClassNames, getMainAppStyles } from './intentGraph.styles';
import { initializeAppAction, resetStoreAction } from './reducers/actions';
import { correlationIdSelector, isComponentInitializedSelector } from './reducers/selectors.redux';

const mapStateToProps = createStructuredSelector({
  isComponentInitialized: isComponentInitializedSelector,
  correlationId: correlationIdSelector
});

const mapDispatchToProps = {
  initializeAppAction,
  resetStoreAction
};

type IIntentGraphProps = ReturnType<typeof mapStateToProps> &
  typeof mapDispatchToProps &
  IStyledComponent<IRootContainerStyles> &
  IComponentProps;

export class IntentGraphBaseC extends React.Component<IIntentGraphProps> {
  private payloadMap = deserializeToImmutableMap(JsonDocument, this.props.staticConfig.payload);
  
  constructor(props: IIntentGraphProps) {
    super(props);
    this.validateSchema();
    this.props.resetStoreAction();
    this.props.initializeAppAction(this.props.staticConfig, this.props.callbackHandlers.LogHandler, this.props.callbackHandlers.IsUserSignedIn);
  }

  public componentDidUpdate = (prevProps: IIntentGraphProps): void => {
    // change in correlationId will reInitialize app
    if (prevProps.correlationId && prevProps.correlationId !== this.props.staticConfig.correlationId) {
      this.validateSchema();
      this.props.initializeAppAction(this.props.staticConfig, this.props.callbackHandlers.LogHandler, this.props.callbackHandlers.IsUserSignedIn);
    }
  };

  public componentWillUnmount(): void {
    this.props.resetStoreAction();
  }

  public render(): React.ReactNode | null {
    const classNames = getMainAppClassNames(this.props.styles, {
      theme: this.props.theme,
      stylesFunc: this.props.staticConfig.customstyle
    });

    return (
      <div className={classNames.root}>
        {this.props.isComponentInitialized && this.props.correlationId === this.props.staticConfig.correlationId && (
          <>
            <Header
              stylesFunc={this.props.staticConfig.customstyle}
              callbackHandlers={this.props.callbackHandlers}
              key={`document$${this.payloadMap.get('meta').get('id')}$header`}
              dialogId={this.payloadMap.get('meta').get('id')}
            />
            <NodesList
              key={`document$${this.payloadMap.get('meta').get('id')}$nodeList`}
              callbackHandlers={this.props.callbackHandlers}
              stylesFunc={this.props.staticConfig.customstyle}
            />
          </>
        )}
      </div>
    );
  }

  private validateSchema = () => {
    const errors = validateIntentSchema(this.props.staticConfig.payload, validationSchema()) || {};
    if (Object.keys(errors).length > 0) {
      if (
        this.props.callbackHandlers.onValidationErrorHandler &&
        typeof this.props.callbackHandlers.onValidationErrorHandler === 'function'
      ) {
        this.props.callbackHandlers.onValidationErrorHandler(errors);
      }
      if(!this.props.staticConfig.disableValidationException){
        throw new Error(JSON.stringify(errors));
      }
    }
  };
}

export const IntentGraphBase = mconnect(
  mapStateToProps,
  mapDispatchToProps
)(styledComponent(IntentGraphBaseC, getMainAppStyles));
